import type { MultipleChoiceExerciseSubmission } from '@org/utils-client';
import { FileText, Video, File as FileIcon } from 'lucide-react';
import { createElement } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TeacherComment {
  comment: string;
  updatedAt: string;
  teacherId: string;
}

export interface GradeStatusTheme {
  badge: string;
  label: string;
}

// ---------------------------------------------------------------------------
// Sorting / formatting helpers
// ---------------------------------------------------------------------------

export function sortByOrder<T extends { order?: number | null }>(items: T[]): T[] {
  return items.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

export function formatOptionLetter(index: number | null | undefined) {
  if (typeof index !== 'number' || index < 0) return '?';
  return String.fromCharCode(65 + (index % 26));
}

export function formatDate(value: string | Date | null | undefined) {
  if (!value) return 'Sem data';
  const date = typeof value === 'string' ? new Date(value) : value;
  return date.toLocaleString();
}

export function formatFileSize(bytes: number | undefined | null): string {
  if (!bytes || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// File type icon helper
// ---------------------------------------------------------------------------

export function getFileTypeIcon(contentType: string) {
  if (contentType === 'application/pdf') {
    return createElement(FileText, { className: 'h-5 w-5 text-red-500' });
  }
  if (contentType?.startsWith('video/')) {
    return createElement(Video, { className: 'h-5 w-5 text-blue-500' });
  }
  if (contentType?.includes('presentation') || contentType?.includes('powerpoint')) {
    return createElement(FileText, { className: 'h-5 w-5 text-orange-500' });
  }
  return createElement(FileIcon, { className: 'h-5 w-5 text-gray-400' });
}

// ---------------------------------------------------------------------------
// Grade → status theme (shared by FillInBlank & SelectForBlank views)
// ---------------------------------------------------------------------------

export function resolveGradeStatusTheme(
  grade: string | undefined,
  isCorrect: boolean,
): GradeStatusTheme {
  switch (grade) {
    case 'perfect':
      return { badge: 'border-emerald-200 bg-emerald-50 text-emerald-700', label: 'Correto (automatico)' };
    case 'near':
      return { badge: 'border-amber-200 bg-amber-50 text-amber-700', label: 'Quase la (IA)' };
    case 'partial':
      return { badge: 'border-amber-200 bg-amber-50 text-amber-700', label: 'Parcial (IA)' };
    case 'wrong':
      return { badge: 'border-rose-200 bg-rose-50 text-rose-700', label: 'Revisar (IA)' };
    default:
      return {
        badge: 'border-slate-200 bg-slate-50 text-slate-700',
        label: isCorrect ? 'Correto' : 'Revisar',
      };
  }
}

// ---------------------------------------------------------------------------
// Evaluation sentence extraction (shared by FillInBlank & SelectForBlank)
// ---------------------------------------------------------------------------

export function extractEvalSentences(
  evaluation: Record<string, unknown> | null,
  expectedKind: string,
): Array<any> {
  const details = ((evaluation?.auto as any)?.details ?? (evaluation as any)?.details) as
    | { kind?: string; sentences?: Array<any> }
    | undefined;
  return details?.kind === expectedKind && Array.isArray(details.sentences) ? details.sentences : [];
}

// ---------------------------------------------------------------------------
// Multiple choice selection resolver
// ---------------------------------------------------------------------------

export function resolveSelectedIndicesFromSubmission(
  submission: MultipleChoiceExerciseSubmission & {
    selectedOptionIndex?: number | null;
    selectedOptionId?: string | null;
  },
  options: Array<{ optionId?: string }>,
): number[] {
  const selectionSet = new Set<number>();

  const addSelection = (index: number | null | undefined) => {
    if (
      typeof index === 'number' &&
      Number.isInteger(index) &&
      index >= 0 &&
      index < options.length &&
      !selectionSet.has(index)
    ) {
      selectionSet.add(index);
    }
  };

  (submission.selectedOptionIndices ?? []).forEach(addSelection);
  addSelection(submission.selectedOptionIndex);

  const optionIds = [
    ...(submission.selectedOptionIds ?? []),
    submission.selectedOptionId ?? undefined,
  ].filter((value): value is string => typeof value === 'string' && value.length > 0);

  optionIds.forEach((optionId) => {
    const matchIndex = options.findIndex((option) => option.optionId === optionId);
    if (matchIndex >= 0) addSelection(matchIndex);
  });

  return Array.from(selectionSet).sort((a, b) => a - b);
}
